import React from 'react'
import { getLocale, history, getIntl } from 'umi'
import type { RequestConfig } from 'umi'
import BrowserLogger from 'alife-logger'

import { Spin } from 'antd'
import Spinner from '@salesforce/design-system-react/components/spinner'
import settings from '@salesforce/design-system-react/components/settings'

import GlobalLoading from '@/components/Loading/global'
import RootContainer from '@/wrappers/RootContainer'

import Message from '@/components/Message'
import STORAGE_MAP from '@/utils/storageMap'
import urlConfig, { PROXY_PREFIX } from '../config/url'

if (process.env.NODE_ENV !== 'test') {
  // @ts-ignore
  settings.setAppElement('#root')
}

;(function mountEnvValue() {
  let headEl: HTMLHeadElement | null = document.querySelector('head')
  const {
    api_host,
    app_domain,
    idaas_pathname,
    down_sample_miniapp,
    down_sample_website,
    arms_pid,
  } = headEl?.dataset || {}

  const nameMap = {
    ApiHost: 'API_HOST',
    AppDomain: 'APP_DOMAIN',
    IdaasPathname: 'IDAAS_PATHNAME',
    DOWN_SAMPLE_MINIAPP: 'DOWN_SAMPLE_MINIAPP',
    DOWN_SAMPLE_WEBSITE: 'DOWN_SAMPLE_WEBSITE',
    ARMS_PID: 'ARMS_PID',
  }

  const isExist = (name: keyof typeof nameMap, value?: string) => {
    const config = (process.env.URL_CONFIG as unknown) as Record<string, string>
    if (!value || value === name) return config?.[nameMap[name]]!
    return value!
  }

  const app = {
    API_HOST: isExist('ApiHost', api_host),
    APP_DOMAIN: isExist('AppDomain', app_domain),
    IDAAS_PATHNAME: isExist('IdaasPathname', idaas_pathname),
    DOWN_SAMPLE_MINIAPP: isExist('DOWN_SAMPLE_MINIAPP', down_sample_miniapp),
    DOWN_SAMPLE_WEBSITE: isExist('DOWN_SAMPLE_WEBSITE', down_sample_website),
    ARMS_PID: isExist('ARMS_PID', arms_pid),
    __VERSION__: process.env.VERSION,
    appendAppDomain(str: string) {
      if (!str?.length) return str
      const isProduction = process.env.NODE_ENV === 'production'
      const prefix = isProduction ? app.APP_DOMAIN : app.APP_DOMAIN.concat(urlConfig.port!)
      const url = prefix.concat(str)
      const protocol = 'https://'
      if (url.startsWith(protocol)) return url
      return protocol.concat(url)
    },
    appendApiDomain(str: string) {
      if (!str?.length) return str
      const isProduction = process.env.NODE_ENV === 'production'
      const prefix = isProduction
        ? app.API_HOST
        : [app.APP_DOMAIN, urlConfig.port, PROXY_PREFIX].join('')
      const url = prefix.concat(str)
      const protocol = 'https://'
      if (isProduction) {
        if (url.startsWith(protocol)) return url
        return protocol.concat(url)
      }
      return url
    },
    appendAppDomainHackLocal(str: string) {
      if (!str?.length) return str
      const isProduction = process.env.NODE_ENV === 'production'
      const prefix = isProduction
        ? app.APP_DOMAIN
        : app.APP_DOMAIN.concat(urlConfig.port!).concat(PROXY_PREFIX)
      const url = prefix.concat(str)
      const protocol = 'https://'
      if (url.startsWith(protocol)) return url
      return protocol.concat(url)
    },
  }

  window.__app = Object.freeze(app)
  Object.defineProperty(window, '__app', {
    writable: false,
    configurable: false,
    enumerable: false,
  })
  Object.seal(window.__app)
  headEl?.removeAttribute('data-api_host')
  headEl?.removeAttribute('data-app_domain')
  headEl?.removeAttribute('data-idaas_pathname')
  headEl?.removeAttribute('down_sample_miniapp')
  headEl?.removeAttribute('down_sample_website')
  setTimeout(() => {
    headEl = null
  })
})()
;(async function initArms() {
  // 非production直接返回
  const isProduction = process.env.NODE_ENV === 'production'
  if (!isProduction) return

  // 解构来自k8s的配置
  const { ARMS_PID } = window.__app
  // 没有参数直接返回
  if (!ARMS_PID) return

  try {
    BrowserLogger.singleton({
      pid: ARMS_PID,
      appType: 'web',
      imgUrl: 'https://arms-retcode.aliyuncs.com/r.png?',
      sendResource: true,
      enableLinkTrace: true,
      behavior: true,
      enableConsole: true,
      useFmp: true,
    })
    // @ts-ignore
    __bl.setConfig({
      ignore: {
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications.',
        ],
      },
    })
  } catch (error) {
    console.error(error)
  }
})()
;(function patchFontFace() {
  const styleNode = document.createElement('style')
  const fontFileName = `/assets/fonts/SalesforceSans-Regular`
  styleNode.innerHTML = `
    @font-face {
      font-family: SalesforceSans-Regular;
      src: url(${fontFileName}.eot);
      src: url(${fontFileName}.eot?#iefix)
          format("embedded-opentype"),
        url("${fontFileName}.woff2")
          format("woff2"),
        url("${fontFileName}.woff")
          format("woff"),
        url("${fontFileName}.ttf")
          format("truetype"),
    }
  `
  requestAnimationFrame(() => {
    document.head.appendChild(styleNode)
  })
})()

Spin.setDefaultIndicator(<Spinner variant="brand" />)

export function onRouteChange({ matchedRoutes }: { matchedRoutes: any }) {
  const routes = matchedRoutes?.map((route: any) => {
    const meta = { ...route.route }
    Reflect.deleteProperty(meta, 'component')
    Reflect.deleteProperty(meta, 'wrappers')
    return meta
  }) as Route[]
  const targetRoute = routes[routes.length - 1]
  if (routes.length) {
    document.title = targetRoute?.title || 'Business Console'
  }
}

export const request: RequestConfig = {
  credentials: 'include',
  timeout: 60 * 1000,
  errorConfig: {
    adaptor: (resData) => {
      return {
        ...resData,
        errorCode: resData.code,
        errorMessage: resData.message,
      }
    },
    errorPage: '',
  },
  /** 自定义err-handler 屏蔽antd 的报错样式 */
  errorHandler: (error) => {
    if (error.toString().includes('AbortError')) {
      return new Promise(() => {})
    }
    if (error.response) {
      // 请求已发送但服务端返回状态码非 2xx 的响应
      console.log('response error', error.response, error.data)
    }
    if (error.message === '505') {
      window.location.pathname = '/maintenance'
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw ''
    }
    throw error // 如果throw. 错误将继续抛出.
  },

  middlewares: [
    async (ctx, next) => {
      const { req } = ctx
      const { url } = req

      const isProduction = process.env.NODE_ENV === 'production'

      ctx.req.url = url.startsWith('http')
        ? url
        : `${isProduction ? window.__app.API_HOST : PROXY_PREFIX}${url}`

      await next()
    },
    async (ctx, next) => {
      await next()
      const { res } = ctx
      /** Error first, 当请求失败时，前面对于传入的Response，注入了 'error' 字段 */
      if (ctx.res?.error) {
        throw res
      }
      /** 如果响应体中没有code码，也就是当请求成功时返回的是直接的resource */
      if (!ctx.res?.code) {
        ctx.res = {
          data: res,
        }
      }
    },
  ],

  requestInterceptors: [
    (url, options) => {
      const language = getLocale()
      const orgId = window.localStorage.getItem(STORAGE_MAP.ORG_ID)
      const mixHeader: Record<string, string> = {
        /** 通过该字段设置后端返回的语言 */
        'Accept-Language': language,
        // 'Accept-Language': 'zh-CN',
        'X-AUTHENTICATION-TYPE': 'cookie',
      }
      if (orgId?.length) {
        mixHeader['x-org-id'] = orgId.replace(/"/g, '')
      }
      const headers = Object.assign(options.headers!, mixHeader)
      return {
        url,
        options: {
          ...options,
          headers,
        },
      }
    },
  ],
  // 这里是fetch原生请求回来的数据
  /** 因为open api不止给前端用，需要对http code status 做判断, 具体的业务data error判断应该在middler-wares里面做 */
  responseInterceptors: [
    // @ts-ignore
    async (response) => {
      switch (response.status) {
        case 200: {
          return response
        }
        case 201: {
          return response
        }
        case 401: {
          window.localStorage.removeItem(STORAGE_MAP.USER_INFO)
          window.localStorage.removeItem(STORAGE_MAP.ORG_ID)
          if (history.location.pathname !== '/login') {
            GlobalLoading.show()
            window.location.href = '/login'
            return {
              error: '401 auth expired, redirect to login...',
            }
          }
          return response
        }
        case 504: {
          Message.error('Gateway timeout!')
          return response
        }
        default: {
          const code =
            response.headers.get('X-ERROR-CODE')?.toString().trim() || response.status.toString()
          if (code === '505' && history.location.pathname !== '/maintenance') {
            throw new Error(code)
          }
          if (code === '202101008' || code === '202101009') {
            if (
              !['/confirm-authenticator', '/bind-authenticator'].includes(history.location.pathname)
            ) {
              history.push('/bind-authenticator')
              return response
            }
          }
          const request_id = response.headers.get('X-REQUEST-ID')
          const intl = getIntl(getLocale())
          const message =
            intl.messages[`ApiErrorCode.${code}`] ||
            response.headers.get('X-ERROR-MESSAGE') ||
            intl.messages[`ApiErrorCode.000`] ||
            response.statusText ||
            'Unknown Error'

          const customResponse = {
            code,
            request_id,
            message,
            error: message || true,
            defaultErrorHandler: () => {
              if (!message) {
                console.log('request error: ', { code, request_id })
                return
              }
              Message.error(message)
            },
          }
          const newResBlob = new Blob([JSON.stringify(customResponse)], {
            type: 'application/json',
          })
          const newRes = new Response(newResBlob, { ...response.clone() })
          return newRes
        }
      }
    },
  ],
}

export function rootContainer(container: any) {
  return React.createElement(RootContainer, container.props, container)
}
